import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import {
  ShieldCheck, Eye, Target, Award, Lock, Zap, Sparkles,
  UserCheck, Building2, Home, Calendar, Crosshair, Truck, Camera,
  Music, BriefcaseBusiness, ArrowRight, Mail, Globe, MapPin,
} from "lucide-react";

const CORE_VALUES = [
  { icon: ShieldCheck, label: "Integrity", body: "We act with honesty in every detail of our operations." },
  { icon: Award, label: "Professionalism", body: "Trained, uniformed, briefed — every guard, every shift." },
  { icon: Eye, label: "Vigilance", body: "Constant awareness from gatehouse to control room." },
  { icon: Target, label: "Discipline", body: "Clear chains of command, documented procedures." },
  { icon: Lock, label: "Confidentiality", body: "Client information is treated as classified material." },
  { icon: Zap, label: "Rapid Response", body: "Minutes matter; our SOPs reflect it." },
  { icon: Sparkles, label: "Excellence", body: "We measure ourselves against the best in the industry." },
];

const SERVICES = [
  { icon: UserCheck, title: "Personal & Executive Protection" },
  { icon: Building2, title: "Commercial Security" },
  { icon: Home, title: "Residential Security" },
  { icon: Calendar, title: "Event Security" },
  { icon: Crosshair, title: "Armed & Unarmed Personnel" },
  { icon: Truck, title: "Mobile Patrol" },
  { icon: Camera, title: "CCTV Monitoring & Surveillance" },
  { icon: ShieldCheck, title: "Logistics Escort" },
  { icon: Music, title: "Nightlife & Venue Security" },
  { icon: BriefcaseBusiness, title: "Security Consulting" },
];

const TARGET_CLIENTS = [
  "Celebrities & entertainers",
  "Government officials",
  "Corporate organizations",
  "Hotels & hospitality businesses",
  "Churches & religious organizations",
  "Construction companies",
  "Warehouses & logistics operators",
  "Retail businesses",
  "High-net-worth individuals",
  "Event organizers",
];

const WHY_CHOOSE_US = [
  "Highly trained security personnel",
  "Fast operational response",
  "Military & law enforcement experience",
  "Professional appearance and conduct",
  "Discreet VIP protection",
  "Affordable and scalable security solutions",
  "Modern surveillance and tactical support",
  "Client-focused operational standards",
];

export const About = () => (
  <div className="min-h-screen bg-navy">
    <AboutHeader />
    <Hero />
    <MissionVision />
    <CoreValues />
    <ServiceLines />
    <WhyAndClients />
    <ContactCTA />
    <AboutFooter />
  </div>
);

const AboutHeader = () => (
  <header style={{ borderBottom: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
      <Logo size={64} />
      <div className="flex items-center gap-5">
        <Link to="/" className="text-muted hover:text-white" style={{ fontSize: 14 }}>Home</Link>
        <Link to="/#services" className="text-muted hover:text-white" style={{ fontSize: 14 }}>Services</Link>
        <Link to="/login" className="btn-outline">Sign in</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
    <div className="badge badge-neutral mb-6 inline-flex items-center gap-2">
      <MapPin size={12} /> Headquartered in Abuja, Nigeria
    </div>
    <h1 style={{ fontSize: 48, lineHeight: 1.1, fontWeight: 500, letterSpacing: "-0.02em", maxWidth: 820 }}>
      A modern private security company built around <span className="text-teal">trust</span>,
      <span className="text-teal"> discipline</span>, and <span className="text-teal">technology</span>.
    </h1>
    <p className="text-muted mt-6" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 720 }}>
      Sirod Secur is a professional private security company specialising in personal
      protection, commercial security, VIP escort services, event security and tactical
      security operations. We safeguard lives, properties, businesses and critical
      assets with integrity and operational excellence — whether the brief is a single
      executive detail or a multi-site residential estate.
    </p>
  </section>
);

const StatementCard = ({ title, body, testId }) => (
  <div className="s-card" style={{ padding: 28 }} data-testid={testId}>
    <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>{title}</div>
    <p style={{ fontSize: 18, lineHeight: 1.5, color: "#fff" }}>{body}</p>
  </div>
);

const MissionVision = () => (
  <section className="max-w-6xl mx-auto px-6 pb-16">
    <div className="grid md:grid-cols-2 gap-5">
      <StatementCard title="Our mission" testId="mission-card"
        body="To provide reliable, professional, and technology-driven security services that ensure the safety of lives, assets, businesses, and communities." />
      <StatementCard title="Our vision" testId="vision-card"
        body="To become one of Nigeria's most trusted and respected private security companies — recognised for excellence, innovation, and operational professionalism." />
    </div>
  </section>
);

const CoreValues = () => (
  <section className="max-w-6xl mx-auto px-6 pb-16">
    <div className="mb-8">
      <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>What we stand for</div>
      <h2 className="h-section" style={{ fontSize: 28 }}>Core values</h2>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {CORE_VALUES.map(v => (
        <div key={v.label} className="s-card" data-testid={`value-${v.label}`}>
          <div className="flex items-center gap-3 mb-2">
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(46,202,200,0.12)",
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
              <v.icon size={16} color="#2ECAC8" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>{v.label}</div>
          </div>
          <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{v.body}</div>
        </div>
      ))}
    </div>
  </section>
);

const ServiceLines = () => (
  <section className="max-w-6xl mx-auto px-6 pb-16">
    <div className="mb-8">
      <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Capability</div>
      <h2 className="h-section" style={{ fontSize: 28 }}>Service lines</h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {SERVICES.map(s => (
        <div key={s.title} className="s-card text-center" style={{ padding: 18 }}>
          <s.icon size={22} color="#2ECAC8" style={{ margin: "0 auto 10px" }} />
          <div style={{ fontSize: 13, color: "#fff", lineHeight: 1.35 }}>{s.title}</div>
        </div>
      ))}
    </div>
  </section>
);

const WhyAndClients = () => (
  <section className="max-w-6xl mx-auto px-6 pb-16">
    <div className="grid md:grid-cols-2 gap-5">
      <div className="s-card" style={{ padding: 28 }}>
        <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Why choose us</div>
        <ul className="space-y-3">
          {WHY_CHOOSE_US.map(item => (
            <li key={item} className="flex items-center gap-3" style={{ fontSize: 14 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ECAC8" }} />
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="s-card" style={{ padding: 28 }}>
        <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Who we serve</div>
        <div className="flex flex-wrap gap-2">
          {TARGET_CLIENTS.map(c => (
            <span key={c} className="badge badge-neutral" style={{ fontSize: 12 }}>{c}</span>
          ))}
        </div>
        <p className="text-muted mt-6" style={{ fontSize: 13, lineHeight: 1.6 }}>
          Whether securing executives, events, residential communities, or commercial
          facilities, our solutions are tailored around professionalism, vigilance, and
          client confidence.
        </p>
      </div>
    </div>
  </section>
);

const ContactCTA = () => (
  <section className="max-w-6xl mx-auto px-6 pb-24">
    <div className="s-card" style={{ padding: 40, textAlign: "center" }}>
      <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Get in touch</div>
      <h3 className="h-section" style={{ fontSize: 28, marginBottom: 18 }}>Ready to be protected?</h3>
      <div className="flex flex-col md:flex-row gap-6 justify-center items-center text-muted" style={{ fontSize: 14 }}>
        <a href="mailto:admin@sirodsecur.com" className="inline-flex items-center gap-2 hover:text-white">
          <Mail size={14} /> admin@sirodsecur.com
        </a>
        <a href="https://www.sirodsecur.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white">
          <Globe size={14} /> www.sirodsecur.com
        </a>
        <span className="inline-flex items-center gap-2">
          <MapPin size={14} /> Abuja, Nigeria
        </span>
      </div>
      <Link to="/login" className="btn-primary inline-flex items-center gap-2 mt-8" data-testid="about-cta">
        Open the platform <ArrowRight size={16} />
      </Link>
    </div>
  </section>
);

const AboutFooter = () => (
  <footer style={{ borderTop: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-muted" style={{ fontSize: 13 }}>
      <div>© 2026 Sirod Secur · Protection you can trust</div>
      <div>Headquarters · Abuja, Nigeria</div>
    </div>
  </footer>
);
